import type { Hemisphere } from '../calendar';

/**
 * The world's geography: seven basins, twelve regions, and the gateways
 * between them (`GDD.md` section 3).
 *
 * Static data, not state: it is the same in every world and is not saved.
 * What changes in a region (its climate, its populations) lives in
 * `World.regions`.
 *
 * **What the latitude limits are for, and what they are not.** They name each
 * region's stretch of ocean and give the clock a latitude for the sun
 * (`clockLatitudeDeg`). They are not a basin outline: the North Pacific's high
 * band and the Arctic overlap in latitude and are different water. Each limit
 * is a named constant below, graded where it is set (review M14 moved them
 * out of the table, where the band boundary was written eleven times).
 */

export type BasinId =
  | 'north_pacific' | 'south_pacific'
  | 'north_atlantic' | 'south_atlantic'
  | 'indian' | 'southern' | 'arctic';

export type RegionId =
  | 'north_pacific_low' | 'north_pacific_high'
  | 'south_pacific_low' | 'south_pacific_high'
  | 'north_atlantic_low' | 'north_atlantic_high'
  | 'south_atlantic_low' | 'south_atlantic_high'
  | 'indian_low' | 'indian_high'
  | 'southern' | 'arctic';

/** Low and mid latitudes, high latitudes, or a polar ocean's single band. */
export type Band = 'low' | 'high' | 'polar';

export interface BasinDef {
  id: BasinId;
  name: string;
  regions: readonly RegionId[];
}

export interface RegionDef {
  id: RegionId;
  basin: BasinId;
  band: Band;
  name: string;
  /** For the calendar's seasons. */
  hemisphere: Hemisphere;
  /** Degrees, north positive. */
  latNorthDeg: number;
  latSouthDeg: number;
}

export type GatewayKind =
  /** Between the two bands of one basin. */
  | 'band'
  /** Across the equator, between a basin's north and south halves. */
  | 'equator'
  /** The Southern Ocean's northern edge, 60°S. */
  | 'southern_edge'
  /** Open water between two southern high bands, in the Antarctic Circumpolar Current. */
  | 'circumpolar'
  /** A strait or a sea passage. */
  | 'passage';

export interface GatewayDef {
  id: string;
  name: string;
  kind: GatewayKind;
  between: readonly [RegionId, RegionId];
  /** Where it is and, where it matters, the source. */
  note: string;
}

export const BASINS: readonly BasinDef[] = [
  { id: 'north_pacific', name: 'North Pacific', regions: ['north_pacific_low', 'north_pacific_high'] },
  { id: 'south_pacific', name: 'South Pacific', regions: ['south_pacific_low', 'south_pacific_high'] },
  { id: 'north_atlantic', name: 'North Atlantic', regions: ['north_atlantic_low', 'north_atlantic_high'] },
  { id: 'south_atlantic', name: 'South Atlantic', regions: ['south_atlantic_low', 'south_atlantic_high'] },
  { id: 'indian', name: 'Indian', regions: ['indian_low', 'indian_high'] },
  { id: 'southern', name: 'Southern', regions: ['southern'] },
  { id: 'arctic', name: 'Arctic', regions: ['arctic'] },
];

/*
 * The latitude limits, degrees north (south negative).
 *
 * The equator and the poles are definitions.
 */
const EQUATOR = 0;
const NORTH_POLE = 90;
/**
 * The boundary between each basin's two bands, both hemispheres. **SET BY
 * HAND** against the transition between the subtropical and subpolar gyres,
 * which lies near 40° in both hemispheres; Phase 1 places it against a
 * climatology (open, M2). One constant, so moving it is one line.
 */
export const BAND_BOUNDARY = 40;
/**
 * The Southern Ocean's northern limit, 60°S: the International Hydrographic
 * Organization's draft fourth edition of *Limits of Oceans and Seas* (S-23; IHO
 * circular letters 1999 to 2001), never formally promulgated but widely used,
 * and the limit the opening brief gave.
 */
const SOUTHERN_OCEAN_NORTH = -60;
/*
 * The outer limits of the high and polar bands, rounded to the degree and used
 * only for the clock's latitude (SET BY HAND to the geography): the Bering
 * Strait near 66°N, the Fram Strait near 79°N, the head of the Arabian Sea and
 * the Bay of Bengal near 25°N, and the Antarctic coast, which runs from about
 * 63°S at the tip of the Peninsula to about 78°S at the Ross Ice Shelf, with
 * 70°S standing for it.
 */
const BERING_STRAIT = 66;
const FRAM_STRAIT = 79;
const INDIAN_NORTH = 25;
const ANTARCTIC_COAST = -70;

export const REGIONS: readonly RegionDef[] = [
  {
    id: 'north_pacific_low', basin: 'north_pacific', band: 'low', hemisphere: 'north',
    name: 'North Pacific, low and mid latitudes', latNorthDeg: BAND_BOUNDARY, latSouthDeg: EQUATOR,
  },
  {
    id: 'north_pacific_high', basin: 'north_pacific', band: 'high', hemisphere: 'north',
    name: 'North Pacific, high latitudes', latNorthDeg: BERING_STRAIT, latSouthDeg: BAND_BOUNDARY,
  },
  {
    id: 'south_pacific_low', basin: 'south_pacific', band: 'low', hemisphere: 'south',
    name: 'South Pacific, low and mid latitudes', latNorthDeg: EQUATOR, latSouthDeg: -BAND_BOUNDARY,
  },
  {
    id: 'south_pacific_high', basin: 'south_pacific', band: 'high', hemisphere: 'south',
    name: 'South Pacific, high latitudes', latNorthDeg: -BAND_BOUNDARY, latSouthDeg: SOUTHERN_OCEAN_NORTH,
  },
  {
    id: 'north_atlantic_low', basin: 'north_atlantic', band: 'low', hemisphere: 'north',
    name: 'North Atlantic, low and mid latitudes', latNorthDeg: BAND_BOUNDARY, latSouthDeg: EQUATOR,
  },
  {
    id: 'north_atlantic_high', basin: 'north_atlantic', band: 'high', hemisphere: 'north',
    name: 'North Atlantic, high latitudes', latNorthDeg: FRAM_STRAIT, latSouthDeg: BAND_BOUNDARY,
  },
  {
    id: 'south_atlantic_low', basin: 'south_atlantic', band: 'low', hemisphere: 'south',
    name: 'South Atlantic, low and mid latitudes', latNorthDeg: EQUATOR, latSouthDeg: -BAND_BOUNDARY,
  },
  {
    id: 'south_atlantic_high', basin: 'south_atlantic', band: 'high', hemisphere: 'south',
    name: 'South Atlantic, high latitudes', latNorthDeg: -BAND_BOUNDARY, latSouthDeg: SOUTHERN_OCEAN_NORTH,
  },
  // The Indian's tropical band spans the equator, from the head of the Arabian
  // Sea and the Bay of Bengal to the band boundary in the south. Most of its water lies
  // south of the equator, so it keeps the southern calendar; its monsoon is a
  // seasonal axis of its own on top (Phase 1, open).
  {
    id: 'indian_low', basin: 'indian', band: 'low', hemisphere: 'south',
    name: 'Indian, tropical and monsoon', latNorthDeg: INDIAN_NORTH, latSouthDeg: -BAND_BOUNDARY,
  },
  {
    id: 'indian_high', basin: 'indian', band: 'high', hemisphere: 'south',
    name: 'Indian, south temperate', latNorthDeg: -BAND_BOUNDARY, latSouthDeg: SOUTHERN_OCEAN_NORTH,
  },
  {
    id: 'southern', basin: 'southern', band: 'polar', hemisphere: 'south',
    name: 'Southern Ocean', latNorthDeg: SOUTHERN_OCEAN_NORTH, latSouthDeg: ANTARCTIC_COAST,
  },
  {
    id: 'arctic', basin: 'arctic', band: 'polar', hemisphere: 'north',
    name: 'Arctic Ocean', latNorthDeg: NORTH_POLE, latSouthDeg: BERING_STRAIT,
  },
];

export const GATEWAYS: readonly GatewayDef[] = [
  // Inside each non-polar basin.
  {
    id: 'north_pacific_bands', name: 'North Pacific, 40°N', kind: 'band',
    between: ['north_pacific_low', 'north_pacific_high'],
    note: 'Between the subtropical and subpolar gyres; Hawaii to Alaska crosses it.',
  },
  {
    id: 'south_pacific_bands', name: 'South Pacific, 40°S', kind: 'band',
    between: ['south_pacific_low', 'south_pacific_high'],
    note: 'Between the subtropical gyre and the subantarctic water.',
  },
  {
    id: 'north_atlantic_bands', name: 'North Atlantic, 40°N', kind: 'band',
    between: ['north_atlantic_low', 'north_atlantic_high'],
    note: 'Between the subtropical and subpolar gyres; the Gulf Stream leaves the coast near it.',
  },
  {
    id: 'south_atlantic_bands', name: 'South Atlantic, 40°S', kind: 'band',
    between: ['south_atlantic_low', 'south_atlantic_high'],
    note: 'Between the subtropical gyre and the subantarctic water.',
  },
  {
    id: 'indian_bands', name: 'Indian, 40°S', kind: 'band',
    between: ['indian_low', 'indian_high'],
    note: 'Between the subtropical gyre and the subantarctic water.',
  },
  // The equator.
  {
    id: 'pacific_equator', name: 'Equatorial Pacific', kind: 'equator',
    between: ['north_pacific_low', 'south_pacific_low'],
    note: 'A real crossing, and a rare one for most species; leatherbacks make it.',
  },
  {
    id: 'atlantic_equator', name: 'Equatorial Atlantic', kind: 'equator',
    between: ['north_atlantic_low', 'south_atlantic_low'],
    note: 'A real crossing, and a rare one for most species.',
  },
  // The Southern Ocean's northern edge.
  {
    id: 'southern_pacific_edge', name: '60°S, Pacific sector', kind: 'southern_edge',
    between: ['southern', 'south_pacific_high'],
    note: 'The Southern Ocean\'s northern limit (IHO S-23 draft, 2000).',
  },
  {
    id: 'southern_atlantic_edge', name: '60°S, Atlantic sector', kind: 'southern_edge',
    between: ['southern', 'south_atlantic_high'],
    note: 'The Southern Ocean\'s northern limit (IHO S-23 draft, 2000).',
  },
  {
    id: 'southern_indian_edge', name: '60°S, Indian sector', kind: 'southern_edge',
    between: ['southern', 'indian_high'],
    note: 'The Southern Ocean\'s northern limit (IHO S-23 draft, 2000).',
  },
  // The subantarctic ring: open water between 40°S and 60°S with the Antarctic
  // Circumpolar Current running east through it. Not in the opening brief's
  // list; added because the three southern high bands touch each other here
  // directly, not only through the Southern Ocean (M2).
  {
    id: 'drake_passage', name: 'Drake Passage, north of 60°S', kind: 'circumpolar',
    between: ['south_pacific_high', 'south_atlantic_high'],
    note: 'Between Cape Horn and 60°S; the circumpolar current runs east through it.',
  },
  {
    id: 'south_of_africa', name: 'South of Africa', kind: 'circumpolar',
    between: ['south_atlantic_high', 'indian_high'],
    note: 'Open water from 40°S to 60°S south of the Cape.',
  },
  {
    id: 'south_of_australia', name: 'South of Australia and Tasmania', kind: 'circumpolar',
    between: ['indian_high', 'south_pacific_high'],
    note: 'Open water from south of Tasmania to 60°S.',
  },
  // The Arctic gateways.
  {
    id: 'bering_strait', name: 'Bering Strait', kind: 'passage',
    between: ['arctic', 'north_pacific_high'],
    note: 'Near 66°N; gray whales feeding in the Chukchi Sea come through it.',
  },
  {
    id: 'nordic_seas', name: 'Fram Strait and the Nordic Seas', kind: 'passage',
    between: ['arctic', 'north_atlantic_high'],
    note: 'The deep gateway, with the Barents Sea opening beside it.',
  },
  {
    id: 'canadian_archipelago', name: 'Canadian Arctic Archipelago to Baffin Bay', kind: 'passage',
    between: ['arctic', 'north_atlantic_high'],
    note: 'The western route, shallow and ice-bound much of the year. Not in the opening '
      + 'brief\'s list; added for the bowhead and narwhal that use it (M2).',
  },
  // Pacific to Indian.
  {
    id: 'makassar_strait', name: 'Indonesian Throughflow, Makassar Strait', kind: 'passage',
    between: ['north_pacific_low', 'indian_low'],
    note: 'The main route, carrying North Pacific water (Gordon & Fine 1996, Nature 379: 146-149).',
  },
  {
    id: 'eastern_indonesian_seas', name: 'Indonesian Throughflow, eastern passages', kind: 'passage',
    between: ['south_pacific_low', 'indian_low'],
    note: 'The lesser routes through the eastern Indonesian seas, from the South Pacific side.',
  },
  // Indian to Atlantic.
  {
    id: 'agulhas', name: 'Agulhas, around the Cape of Good Hope', kind: 'passage',
    between: ['indian_low', 'south_atlantic_low'],
    note: 'Indian Ocean water leaks into the South Atlantic around the Cape.',
  },
];

const REGION_BY_ID = new Map(REGIONS.map((r) => [r.id, r]));
const BASIN_BY_ID = new Map(BASINS.map((b) => [b.id, b]));

export function region(id: RegionId): RegionDef {
  const r = REGION_BY_ID.get(id);
  if (!r) throw new Error(`No region ${id}`);
  return r;
}

export function basin(id: BasinId): BasinDef {
  const b = BASIN_BY_ID.get(id);
  if (!b) throw new Error(`No basin ${id}`);
  return b;
}

/**
 * The latitude the clock computes a region's sun at: the middle of its band.
 * A readout of the calendar's light, not a climate: Phase 1 gives each region
 * a climatology of its own.
 */
export function clockLatitudeDeg(r: RegionDef): number {
  return (r.latNorthDeg + r.latSouthDeg) / 2;
}

/** The gateways that touch a region. */
export function gatewaysOf(id: RegionId): GatewayDef[] {
  return GATEWAYS.filter((g) => g.between[0] === id || g.between[1] === id);
}
